// MKS-207 (billing revision B) — the invoice REVIEW panel on /invoices/new.
//
// Before issuing an invoice the PM/admin sees, for one project and one period,
// every billable item and its state, ticks what goes on the invoice, and can
// read why the invoice total differs from the sum of the timesheets.
//
// Groups: the whole retainer · overage (with a drill-down to entries) · T&M
// (with a drill-down per person) · costs per document. Each carries a checkbox
// and one of three states:
//   🟢 ready    nothing stands in the way
//   🟡 waiting  hours not approved, or a timesheet still open
//   🔴 blocked  no rate — the hours would bill at zero
//
// Blocked items are UNTICKED by default and the page refuses to generate over
// one until the operator confirms explicitly (see `selectionHasBlockedItem`).
//
// The panel is controlled: it owns no selection state. The page holds the
// selection because the same object is what it sends to `previewInvoice` and
// `generateInvoice` as the `InclusionDescriptor`, which is what makes the
// totals update live as checkboxes move.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Pill } from '../ui/Pill';
import { formatCurrency, formatDisplayDate } from '../../utils/format';
import type {
  BillableEntryState,
  BillingItemState,
  ContractValidity,
  HourlyUserRow,
  ProjectToBeInvoicedData,
} from '../../services/api/toBeInvoiced';
import { LEGACY_EN_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

// ===== Selection =====

/**
 * The client-side mirror of `InclusionDescriptor`. `entryIds: undefined` means
 * "no entry-level restriction" — the Hour Package case, where the period's
 * entries stay linked for audit and must not be filtered.
 */
export interface ReviewSelection {
  package: boolean;
  overage: boolean;
  expenseReceiptIds: number[];
  entryIds: number[] | undefined;
}

// ===== State presentation =====

const ITEM_STATE_COLORS: Record<BillingItemState, { bg: string; fg: string; dot: string }> = {
  ready: { bg: '#D1FAE5', fg: '#065F46', dot: '#10B981' },
  waiting: { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
  blocked: { bg: '#FEE2E2', fg: '#991B1B', dot: '#EF4444' },
};

const ITEM_STATE_LABELS: Record<BillingItemState, string> = {
  ready: 'Ready',
  waiting: 'Waiting',
  blocked: 'Blocked',
};

export function ItemStatePill({ state }: { state: BillingItemState }) {
  const colors = ITEM_STATE_COLORS[state];
  return (
    <Pill bg={colors.bg} fg={colors.fg} dot={colors.dot}>
      {ITEM_STATE_LABELS[state]}
    </Pill>
  );
}

/**
 * MKS-269 — how an entry state reads next to a person's name. The readiness
 * pill answers "can this be billed cleanly"; this answers "what did approval
 * do with it", which is the thing the operator is deciding about.
 */
const ENTRY_STATE_LABELS: Record<BillableEntryState, string> = {
  approved: 'approved',
  submitted: 'submitted, not approved',
  open: 'open, not submitted',
  rejected: 'rejected',
};

/** Only approved hours go on the invoice unless the operator says otherwise. */
export function isApprovedRow(person: HourlyUserRow): boolean {
  return (person.entryState ?? 'approved') === 'approved';
}

const CONTRACT_NOTICES: Record<ContractValidity, string | null> = {
  valid: null,
  unset: null,
  expired: 'This project’s contract ended before the selected period.',
  not_started: 'This project’s contract starts after the selected period.',
};

// ===== Pure helpers (exported so the page can gate Generate on them) =====

/**
 * The default tick state: everything that is ready or waiting goes on the
 * invoice, everything blocked stays off. A blocked item is one that would bill
 * at zero, so defaulting it ON is exactly the mistake this page exists to stop.
 *
 * MKS-269 — and, among the hours, only the APPROVED ones. The page now lists
 * every hour of the period per person and state; submitted / open / rejected
 * hours are there to be seen and, if the operator decides so, ticked — never to
 * arrive pre-ticked on a client's invoice.
 */
export function defaultSelection(data: ProjectToBeInvoicedData): ReviewSelection {
  const isBalicek = !!data.package;
  const hourly = data.hourlyEntries;
  const carried = data.carriedOver;

  // Hour Package projects never send `entryIds`: the period's entries stay
  // linked to the paushal invoice for audit, exactly as the project card has
  // always sent it.
  //
  // MKS-280 — carried-over hours are ticked by default like any other
  // approved, unblocked hours. That is the promise the copy makes: something
  // left unticked last month "goes out on the next one". A month whose own
  // hours are empty but which carries a leftover still gets an entry-level
  // selection, so the leftover is what the invoice bills.
  let entryIds: number[] | undefined;
  if (!isBalicek && (hourly || carried)) {
    entryIds = [...defaultTickedIds(hourly), ...defaultTickedIds(carried)];
  }

  return {
    package: data.package ? data.package.state !== 'blocked' : false,
    overage: data.overage ? data.overage.state === 'ready' : false,
    expenseReceiptIds: (data.expenses ?? [])
      .filter((r) => r.state !== 'blocked')
      .map((r) => r.id),
    entryIds,
  };
}

/**
 * The entries a group contributes to the default tick state: the approved,
 * unblocked ones. Shared by the period's own hours and by the carried-over
 * ones, which tick by exactly the same rule.
 */
function defaultTickedIds(
  group: { entryIds?: number[]; byUser?: HourlyUserRow[] } | undefined,
): number[] {
  if (!group) return [];
  if (!group.byUser) return group.entryIds ?? [];
  return group.byUser
    .filter((u) => u.state !== 'blocked' && isApprovedRow(u))
    .flatMap((u) => u.entryIds);
}

/**
 * MKS-249 — what the operator left unticked, expressed in the terms the
 * reconciliation table speaks: timesheet hours and the billing stored on them.
 *
 * The table used to read "billable this period, minus the discount, equals the
 * invoice" — true only while the ticks were ignored by the API. Now that they
 * are honoured, unticking is the other thing that moves the total, so it needs
 * a line of its own or the block no longer adds up.
 *
 * Only the T&M / hourly rows count. The reconciliation figures are built from
 * `entry` rows alone (`computeReconciliation`), so an unticked receipt or
 * retainer has no positive line above it here to subtract from.
 *
 * `undefined` means the selection carries no entry-level restriction (the Hour
 * Package case) — nothing can be unticked, so there is no line to show.
 */
export function untickedHourly(
  byUser: HourlyUserRow[],
  entryIds: number[] | undefined,
): { hours: number; billing: number } | null {
  if (entryIds === undefined) return null;
  // MKS-269 — APPROVED rows only. The unapproved ones were never part of
  // "Billable this period" (the line this subtracts from), so counting them
  // here would subtract the same hours the "Not approved yet" line above
  // already took off, and the chain would undershoot the preview by exactly
  // the unapproved amount.
  return prorateRows(byUser.filter(isApprovedRow), entryIds, (id, ticked) => !ticked.has(id));
}

/**
 * MKS-269 — the other half of the new arithmetic: hours the operator ticked ON
 * although approval has not cleared them.
 *
 * They are absent from "Billable this period" and were already subtracted by
 * "Not approved yet", so the only way the chain can still land on the previewed
 * total is to add them back as their own, positive line. Returns `null` when
 * nothing is entry-restricted (the Hour Package case) or when nothing
 * unapproved is ticked — there is then no line to show.
 */
export function tickedUnapproved(
  byUser: HourlyUserRow[],
  entryIds: number[] | undefined,
): { hours: number; billing: number } | null {
  if (entryIds === undefined) return null;
  const result = prorateRows(
    byUser.filter((u) => !isApprovedRow(u)),
    entryIds,
    (id, ticked) => ticked.has(id),
  );
  if (result === null || (result.hours === 0 && result.billing === 0)) return null;
  return result;
}

/**
 * MKS-280 — the carried-over hours the operator has ticked ON.
 *
 * Every reconciliation line above it is about THIS month's timesheets, so an
 * older month's hours appear nowhere in the walk — yet they are on the
 * invoice, and the preview total includes them. Without this positive line
 * the chain undershoots the preview by exactly the leftover.
 *
 * Returns `null` when nothing is carried over or nothing of it is ticked.
 */
export function tickedCarriedOver(
  byUser: HourlyUserRow[],
  entryIds: number[] | undefined,
): { hours: number; billing: number } | null {
  if (entryIds === undefined || byUser.length === 0) return null;
  const result = prorateRows(byUser, entryIds, (id, ticked) => ticked.has(id));
  if (result.hours === 0 && result.billing === 0) return null;
  return result;
}

/**
 * Sum the rows' hours and billing over the entries matching `counts`.
 *
 * A person-and-state row is the unit the panel ticks, so an all-or-nothing row
 * counts in full. A partially ticked one — only reachable through a carried
 * `?entryIds=` link — is prorated by entry count: per-entry billing is not in
 * this payload, and reading the row as all-or-nothing would overstate the line.
 */
function prorateRows(
  rows: HourlyUserRow[],
  entryIds: number[],
  counts: (id: number, ticked: Set<number>) => boolean,
): { hours: number; billing: number } {
  const ticked = new Set(entryIds);
  let hours = 0;
  let billing = 0;
  for (const person of rows) {
    if (person.entryIds.length === 0) continue;
    const matching = person.entryIds.filter((id) => counts(id, ticked)).length;
    if (matching === 0) continue;
    const share = matching / person.entryIds.length;
    hours += person.hours * share;
    billing += person.billing * share;
  }
  return { hours, billing };
}

/**
 * Is anything currently ticked a 🔴 item? The page uses this to require an
 * explicit confirmation before it will issue the invoice.
 */
export function selectionHasBlockedItem(
  data: ProjectToBeInvoicedData,
  selection: ReviewSelection,
): boolean {
  if (selection.package && data.package?.state === 'blocked') return true;
  if (selection.overage && data.overage?.state === 'blocked') return true;
  if (
    (data.expenses ?? []).some(
      (r) => r.state === 'blocked' && selection.expenseReceiptIds.includes(r.id),
    )
  ) {
    return true;
  }
  // MKS-280 — a carried-over row can be 🔴 for the same reason a period row
  // can (nobody has a rate), and it bills on the same invoice, so it has to
  // trip the same confirmation.
  const byUser = [...(data.hourlyEntries?.byUser ?? []), ...(data.carriedOver?.byUser ?? [])];
  const ticked = new Set(selection.entryIds ?? []);
  // `entryIds: undefined` means "no restriction", so every blocked person's
  // hours are in: the Hour Package audit-link case cannot hide a 🔴 row.
  const unrestricted = selection.entryIds === undefined;
  return byUser.some(
    (u) => u.state === 'blocked' && (unrestricted || u.entryIds.some((id) => ticked.has(id))),
  );
}

// ===== Component =====

export interface InvoiceReviewPanelProps {
  projectId: number;
  data: ProjectToBeInvoicedData;
  selection: ReviewSelection;
  onSelectionChange: (next: ReviewSelection) => void;
  /** False for a viewer who may not invoice — the whole panel goes read-only. */
  canManage: boolean;
  /** The previewed invoice total for this project, in CZK; null before a preview. */
  previewTotal: number | null;
  /** The discount the preview applied (Phase 110), in CZK. */
  previewDiscount?: number;
}

export function InvoiceReviewPanel({
  projectId,
  data,
  selection,
  onSelectionChange,
  canManage,
  previewTotal,
  previewDiscount,
}: InvoiceReviewPanelProps) {
  const [overageOpen, setOverageOpen] = useState(false);
  const [hourlyOpen, setHourlyOpen] = useState(false);
  // MKS-280 — open by default: a leftover from a month nobody is looking at
  // any more is exactly the thing the operator needs to see, not unfold.
  const [carriedOpen, setCarriedOpen] = useState(true);

  const pkg = data.package;
  const ovg = data.overage;
  const hourly = data.hourlyEntries;
  const carried = data.carriedOver;
  const carriedByUser = carried?.byUser ?? [];
  const expenses = data.expenses ?? [];
  const byUser = hourly?.byUser ?? [];
  const openTimesheets = data.openTimesheets ?? [];
  const unlogged = data.unloggedAssignees ?? [];
  const contractNotice = CONTRACT_NOTICES[data.contractValidity ?? 'unset'];
  // MKS-252 — hours booked in this period that approval has not cleared. The
  // panel used to ignore them entirely, so a project whose month was half
  // unapproved still read 🟢 ready all the way down.
  const unapprovedHours = data.unapprovedHours ?? 0;
  // MKS-269 — is this the per-state payload? One flag-on row is enough: the
  // backend either stamps `entryState` on every row or on none.
  const perStateRows = byUser.some((u) => u.entryState != null);

  const set = (patch: Partial<ReviewSelection>) => {
    if (!canManage) return;
    onSelectionChange({ ...selection, ...patch });
  };

  const tickedEntryIds = new Set(selection.entryIds ?? []);
  const userTicked = (entryIds: number[]) =>
    selection.entryIds === undefined || entryIds.every((id) => tickedEntryIds.has(id));

  const toggleUser = (entryIds: number[], checked: boolean) => {
    // Only meaningful when the selection is entry-restricted at all; an Hour
    // Package project never offers these checkboxes.
    if (selection.entryIds === undefined) return;
    const next = new Set(selection.entryIds);
    for (const id of entryIds) {
      if (checked) next.add(id);
      else next.delete(id);
    }
    set({ entryIds: [...next] });
  };

  const allExpensesTicked =
    expenses.length > 0 && expenses.every((r) => selection.expenseReceiptIds.includes(r.id));

  return (
    <section
      className="bg-white rounded-lg border border-gray-200 p-6 space-y-5"
      aria-label="Invoice review"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-gray-900">Review what goes on the invoice</h2>
        {!canManage && (
          <span className="text-xs text-gray-500">
            Read-only — you are not allowed to issue invoices for this project.
          </span>
        )}
      </div>

      {contractNotice && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          role="status"
        >
          {contractNotice}
        </div>
      )}

      {/* MKS-252 — hours the invoice will leave behind because nobody has
          approved them yet. Shown even when nothing else on the page is
          yellow: it is the one thing an operator can still go and fix. */}
      {unapprovedHours > 0 && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          role="status"
        >
          {/* MKS-269 — once the hours are listed per state below, they are no
              longer simply excluded: they are unticked, and the operator may
              tick them. Saying "not on this invoice" would then be a statement
              the page itself can contradict two clicks later. */}
          {perStateRows
            ? `${unapprovedHours.toFixed(2)} h in this period are not approved yet — they are listed below, unticked. Tick them only if they really should be billed; ticking does not approve them.`
            : `${unapprovedHours.toFixed(2)} h in this period are not approved yet — they are not on this invoice and come back in next period’s queue.`}
        </div>
      )}

      {/* ===== Retainer ===== */}
      {pkg && (
        <ReviewRow
          id={`review-package-${projectId}`}
          label="Retainer"
          state={pkg.state ?? 'ready'}
          checked={selection.package}
          disabled={!canManage}
          onChange={(checked) => set({ package: checked })}
          detail={`${pkg.balicekHours} h · ${formatNumber(pkg.paushalAmount, undefined, LEGACY_EN_NUMBER_LOCALE)} ${pkg.paushalCurrency}`}
        />
      )}

      {/* ===== Overage, with a drill-down to entries ===== */}
      {ovg && (
        <div>
          <ReviewRow
            id={`review-overage-${projectId}`}
            label="Overage"
            state={ovg.state ?? 'ready'}
            checked={selection.overage}
            disabled={!canManage}
            onChange={(checked) => set({ overage: checked })}
            detail={`${ovg.totalHours.toFixed(2)} h · ${formatCurrency(ovg.totalBilling)}`}
            action={
              ovg.overageStatus === 'needs_approval' ? (
                <Link
                  to={`/projects/${projectId}/monthly-overage-approvals`}
                  className="text-[12px] text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Approve overage →
                </Link>
              ) : null
            }
          />
          <DrillDownToggle
            open={overageOpen}
            onToggle={() => setOverageOpen((o) => !o)}
            label="overage entries"
          />
          {overageOpen && (
            <div className="ml-6 mt-2">
              {ovg.entries.length === 0 ? (
                <p className="text-[12px] text-gray-500 italic">No overage hours this period.</p>
              ) : (
                <table className="w-full text-[10.5px] tabular-nums">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-200">
                      <th className="text-left py-1 pr-2 font-semibold">Person</th>
                      <th className="text-left py-1 pr-2 font-semibold">Date</th>
                      <th className="text-right py-1 pr-2 font-semibold">Hours</th>
                      <th className="text-left py-1 pr-2 font-semibold">Role</th>
                      <th className="text-right py-1 pr-2 font-semibold">Rate/h</th>
                      <th className="text-right py-1 font-semibold">Billing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ovg.entries.map((entry) => (
                      <tr key={entry.entryId} className="border-b border-gray-100">
                        <td className="py-1 pr-2 text-gray-600">{entry.userName}</td>
                        <td className="py-1 pr-2 text-gray-600">{entry.date}</td>
                        <td className="py-1 pr-2 text-right">{entry.overageSlice.toFixed(2)}</td>
                        <td className="py-1 pr-2 text-gray-600">{entry.roleName ?? '—'}</td>
                        <td className="py-1 pr-2 text-right">
                          {entry.hasRate ? formatCurrency(entry.hourlyRate) : 'No rate'}
                        </td>
                        <td className="py-1 text-right">
                          {entry.hasRate ? formatCurrency(entry.billing) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== T&M / hourly, with a drill-down per person ===== */}
      {hourly && (
        <div>
          <ReviewRow
            id={`review-hourly-${projectId}`}
            label="Time &amp; material"
            state={hourly.state ?? 'ready'}
            checked={(selection.entryIds?.length ?? 0) > 0 || selection.entryIds === undefined}
            disabled={!canManage || selection.entryIds === undefined}
            onChange={(checked) =>
              set({
                // MKS-269 — "tick the group" means the default selection:
                // approved, unblocked hours. Unapproved ones stay a deliberate
                // per-row decision, never something a group toggle sweeps on.
                entryIds: checked
                  ? byUser
                      .filter((u) => u.state !== 'blocked' && isApprovedRow(u))
                      .flatMap((u) => u.entryIds)
                  : [],
              })
            }
            detail={`${hourly.totalHours.toFixed(2)} h · ${formatCurrency(hourly.totalBilling)}`}
          />
          {byUser.length > 0 && (
            <>
              <DrillDownToggle
                open={hourlyOpen}
                onToggle={() => setHourlyOpen((o) => !o)}
                label="hours per person"
              />
              {hourlyOpen && (
                <ul className="ml-6 mt-2 space-y-1">
                  {/* MKS-269 — one row per person AND entry state, so
                      "Jan · approved 61.5 h" and "Jan · submitted 3 h" are two
                      independent ticks. The key carries the state for the same
                      reason. */}
                  {byUser.map((person) => {
                    const rowId = `review-person-${person.userId}${
                      person.entryState ? `-${person.entryState}` : ''
                    }`;
                    return (
                      <li key={rowId} className="flex items-center gap-2 text-[12px] text-gray-700">
                        <input
                          type="checkbox"
                          id={rowId}
                          checked={userTicked(person.entryIds)}
                          disabled={!canManage || selection.entryIds === undefined}
                          onChange={(e) => toggleUser(person.entryIds, e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <label htmlFor={rowId} className="cursor-pointer">
                          {person.userName}
                          {person.entryState && (
                            <span className="text-gray-500">
                              {' '}
                              · {ENTRY_STATE_LABELS[person.entryState]}
                            </span>
                          )}
                        </label>
                        <span className="tabular-nums text-gray-500">
                          {person.hours.toFixed(2)} h · {formatCurrency(person.billing)}
                        </span>
                        <ItemStatePill state={person.state} />
                        {person.blocked && (
                          <span className="text-[10.5px] text-red-700">
                            no rate — would bill at 0
                          </span>
                        )}
                        {/* The per-person "N h not approved" note belongs to the
                            pre-MKS-269 payload, where a person had one row and
                            their unapproved hours had nowhere else to show. Once
                            the row names its own state, repeating it is noise. */}
                        {!person.entryState && (person.unapprovedHours ?? 0) > 0 && (
                          <span className="text-[10.5px] text-amber-700">
                            {(person.unapprovedHours ?? 0).toFixed(2)} h not approved
                          </span>
                        )}
                        {person.openTimesheet && (
                          <span className="text-[10.5px] text-amber-700">timesheet still open</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== MKS-280 — left over from a month that is already invoiced =====

          Its own group, not part of "Time & material" above: these hours
          belong to a different month, and the reconciliation table below
          walks THIS month's timesheets. Folding them in would make that walk
          stop adding up, which is the one thing it exists to do. */}
      {carried && (
        <div data-testid="review-carried-over">
          <ReviewRow
            id={`review-carried-${projectId}`}
            label={`Carried over from ${carried.periods.join(', ')}`}
            state={carried.state ?? 'ready'}
            checked={carried.entryIds.some((id) => tickedEntryIds.has(id))}
            disabled={!canManage || selection.entryIds === undefined}
            onChange={(checked) =>
              toggleUser(
                carriedByUser.length > 0
                  ? carriedByUser
                      .filter((u) => u.state !== 'blocked' && isApprovedRow(u))
                      .flatMap((u) => u.entryIds)
                  : carried.entryIds,
                checked,
              )
            }
            detail={`${carried.totalHours.toFixed(2)} h · ${formatCurrency(carried.totalBilling)}`}
          />
          <p className="ml-6 mt-1 text-[11px] text-gray-500">
            Still unbilled after that month’s invoice — left unticked then, or approved after it
            went out. Ticked here, they go on this invoice.
          </p>
          {carriedByUser.length > 0 && (
            <>
              <DrillDownToggle
                open={carriedOpen}
                onToggle={() => setCarriedOpen((o) => !o)}
                label="carried-over hours per person"
              />
              {carriedOpen && (
                <ul className="ml-6 mt-2 space-y-1">
                  {carriedByUser.map((person) => {
                    const rowId = `review-carried-person-${person.userId}${
                      person.entryState ? `-${person.entryState}` : ''
                    }`;
                    return (
                      <li key={rowId} className="flex items-center gap-2 text-[12px] text-gray-700">
                        <input
                          type="checkbox"
                          id={rowId}
                          checked={userTicked(person.entryIds)}
                          disabled={!canManage || selection.entryIds === undefined}
                          onChange={(e) => toggleUser(person.entryIds, e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <label htmlFor={rowId} className="cursor-pointer">
                          {person.userName}
                          {person.entryState && (
                            <span className="text-gray-500">
                              {' '}
                              · {ENTRY_STATE_LABELS[person.entryState]}
                            </span>
                          )}
                        </label>
                        <span className="tabular-nums text-gray-500">
                          {person.hours.toFixed(2)} h · {formatCurrency(person.billing)}
                        </span>
                        <ItemStatePill state={person.state} />
                        {person.blocked && (
                          <span className="text-[10.5px] text-red-700">
                            no rate — would bill at 0
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== Costs, per document ===== */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            id={`review-expenses-${projectId}`}
            checked={allExpensesTicked}
            disabled={!canManage || expenses.length === 0}
            onChange={(e) =>
              set({ expenseReceiptIds: e.target.checked ? expenses.map((r) => r.id) : [] })
            }
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label
            htmlFor={`review-expenses-${projectId}`}
            className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-500"
          >
            External costs
          </label>
        </div>
        {expenses.length === 0 ? (
          <p className="ml-6 text-[12px] text-gray-500 italic">
            No expense receipts for this period.
          </p>
        ) : (
          <ul className="ml-6 space-y-1">
            {expenses.map((receipt) => (
              <li key={receipt.id} className="flex items-center gap-2 text-[12px] text-gray-700">
                <input
                  type="checkbox"
                  id={`review-receipt-${receipt.id}`}
                  checked={selection.expenseReceiptIds.includes(receipt.id)}
                  disabled={!canManage}
                  onChange={(e) =>
                    set({
                      expenseReceiptIds: e.target.checked
                        ? [...selection.expenseReceiptIds, receipt.id]
                        : selection.expenseReceiptIds.filter((id) => id !== receipt.id),
                    })
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor={`review-receipt-${receipt.id}`} className="cursor-pointer">
                  {receipt.description ?? '—'}
                </label>
                <span className="text-gray-500">{formatDisplayDate(receipt.receiptDate)}</span>
                <span className="tabular-nums text-gray-500">
                  {receipt.billedAmount != null || receipt.amount != null
                    ? formatCurrency(receipt.billedAmount ?? receipt.amount ?? 0)
                    : 'no amount'}
                </span>
                <ItemStatePill state={receipt.state ?? 'ready'} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ===== Who is still holding the period up ===== */}
      <div className="grid gap-4 md:grid-cols-2 border-t border-gray-100 pt-4">
        <PeopleList
          title="Open timesheets"
          empty="Every timesheet for this period is approved."
          items={openTimesheets.map((p) => `${p.userName} (${p.state})`)}
        />
        <PeopleList
          title="Assigned but logged nothing"
          empty="Everybody assigned in this period logged something."
          items={unlogged.map((p) => (p.roleName ? `${p.userName} — ${p.roleName}` : p.userName))}
        />
      </div>

      {/* ===== Reconciliation ===== */}
      {data.reconciliation && (
        <Reconciliation
          reconciliation={data.reconciliation}
          previewTotal={previewTotal}
          previewDiscount={previewDiscount}
          unticked={untickedHourly(byUser, selection.entryIds)}
          tickedUnapproved={tickedUnapproved(byUser, selection.entryIds)}
          carriedOver={
            carried
              ? {
                  periods: carried.periods,
                  ticked: tickedCarriedOver(carriedByUser, selection.entryIds),
                }
              : null
          }
        />
      )}
    </section>
  );
}

// ===== Sub-components =====

function ReviewRow({
  id,
  label,
  state,
  checked,
  disabled,
  onChange,
  detail,
  action,
}: {
  id: string;
  label: string;
  state: BillingItemState;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  detail: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={id} className="text-sm font-semibold text-gray-900 cursor-pointer">
          {label}
        </label>
        <ItemStatePill state={state} />
        <span className="text-[12px] tabular-nums text-gray-600">{detail}</span>
        {action}
      </div>
    </div>
  );
}

function DrillDownToggle({
  open,
  onToggle,
  label,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="ml-6 mt-1 flex items-center gap-1 text-[12px] text-blue-600 hover:text-blue-800"
    >
      {open ? (
        <ChevronUpIcon className="h-3.5 w-3.5" />
      ) : (
        <ChevronDownIcon className="h-3.5 w-3.5" />
      )}
      {open ? `Hide ${label}` : `Show ${label}`}
    </button>
  );
}

function PeopleList({
  title,
  empty,
  items,
}: {
  title: string;
  empty: string;
  items: string[];
}) {
  return (
    <div>
      <p className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
        {title}
      </p>
      {items.length === 0 ? (
        <p className="text-[12px] text-gray-500 italic">{empty}</p>
      ) : (
        <ul className="text-[12px] text-gray-700 space-y-0.5">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * "Why isn't the invoice the sum of the timesheets?" — the one question this
 * page exists to answer, and the one the UI said nothing about before.
 *
 * Reads top-down: everything logged in the period, minus the hours still
 * waiting for approval, minus what an earlier invoice already carried, leaves
 * what is billable now; the discount and anything left unticked explain the
 * rest of the distance to the previewed total.
 */
function Reconciliation({
  reconciliation,
  previewTotal,
  previewDiscount,
  unticked,
  tickedUnapproved,
  carriedOver,
}: {
  reconciliation: NonNullable<ProjectToBeInvoicedData['reconciliation']>;
  previewTotal: number | null;
  previewDiscount?: number;
  /** MKS-249 — `null` when nothing on screen can be unticked. */
  unticked: { hours: number; billing: number } | null;
  /** MKS-269 — `null` when no unapproved hours are ticked. */
  tickedUnapproved: { hours: number; billing: number } | null;
  /** MKS-280 — `null` when no earlier month left anything behind. */
  carriedOver: {
    periods: string[];
    ticked: { hours: number; billing: number } | null;
  } | null;
}) {
  const r = reconciliation;
  const rows: { label: string; hours?: number; amount: number; muted?: boolean }[] = [
    { label: 'Logged in the period (all timesheets)', hours: r.loggedHours, amount: r.loggedBilling },
    {
      label: 'Not approved yet',
      hours: -r.unapprovedHours,
      amount: -r.unapprovedBilling,
      muted: true,
    },
    {
      label: 'Already on an earlier invoice',
      hours: -r.alreadyInvoicedHours,
      amount: -r.alreadyInvoicedBilling,
      muted: true,
    },
    {
      label: 'Billable this period',
      hours: r.approvedUninvoicedHours,
      amount: r.approvedUninvoicedBilling,
    },
  ];
  // MKS-249 — between "billable" and the discount, because that is where it
  // sits in the arithmetic: billable this period, minus what was left
  // unticked, minus the discount, is the previewed total. Shown even at zero
  // so the subtraction chain never has a silent gap in it.
  if (unticked) {
    // `-0` renders as "-0,00 Kč"; keep the zero case plain.
    const negate = (n: number) => (n === 0 ? 0 : -n);
    rows.push({
      label: 'Left unticked above',
      hours: negate(unticked.hours),
      amount: negate(unticked.billing),
      muted: true,
    });
  }
  // MKS-269 — the operator deliberately put hours on the invoice that approval
  // has not cleared. "Not approved yet" above already took them off, so they
  // come back here as a positive line; without it the chain lands short of the
  // previewed total by exactly this amount.
  if (tickedUnapproved) {
    rows.push({
      label: 'Billed although not approved',
      hours: tickedUnapproved.hours,
      amount: tickedUnapproved.billing,
      muted: true,
    });
  }
  // MKS-280 — an older month's hours appear nowhere in the lines above, which
  // are all about THIS month's timesheets, yet they are on the invoice. They
  // come in here as their own positive line, for the same reason
  // "Billed although not approved" does.
  if (carriedOver?.ticked) {
    rows.push({
      label: `Carried over from ${carriedOver.periods.join(', ')}`,
      hours: carriedOver.ticked.hours,
      amount: carriedOver.ticked.billing,
      muted: true,
    });
  }
  if (previewDiscount != null && previewDiscount !== 0) {
    rows.push({ label: 'Discount', amount: -previewDiscount, muted: true });
  }

  return (
    <div className="border-t border-gray-100 pt-4">
      <p className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
        Timesheets vs. invoice
      </p>
      <table className="w-full text-[12px] tabular-nums">
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className={row.muted ? 'text-gray-500' : 'text-gray-900'}>
              <td className="py-0.5">{row.label}</td>
              <td className="py-0.5 text-right w-24">
                {row.hours != null ? `${row.hours.toFixed(2)} h` : ''}
              </td>
              <td className="py-0.5 text-right w-32">{formatCurrency(row.amount)}</td>
            </tr>
          ))}
          <tr className="font-semibold text-gray-900 border-t border-gray-200">
            <td className="py-1">Invoice total (preview)</td>
            <td />
            <td className="py-1 text-right">
              {previewTotal != null ? formatCurrency(previewTotal) : '—'}
            </td>
          </tr>
        </tbody>
      </table>
      {/* MKS-280 — this sentence was a promise the queue did not keep: once a
          period had an invoice it dropped the project entirely, so the
          leftover came back nowhere. It does now, named as "carried over"
          on the next month's row and tickable on its review panel. */}
      <p className="mt-2 text-[11px] text-gray-500">
        Anything left unticked above is not discarded: it keeps no invoice link and comes back in
        next period’s queue, listed there as carried over from this month.
      </p>
    </div>
  );
}
